/**
 * admin-svc /admin/agent-disclosure — the dual-capacity disclosure's two
 * questions (V085).
 *
 * MLD Form 505 rides a PURCHASE_LOAN packet whenever one licence holder is both
 * the real estate sales agent and the mortgage loan originator. Two of its boxes
 * cannot be derived from anything we hold, so the agent answers them once on
 * their profile and every disclosure they send carries the answer.
 *
 * Unlike the rate card, these are NOT "null = inherit a brokerage default":
 * `mortgage_re_relationship` always has a value, because a disclosure with
 * neither box ticked is not a disclosure.
 *
 * An agent omits the target and reaches their own; a brokerage_admin /
 * super_admin must name the agent.
 */

import type { ApiClient } from "../client";
import type { RateCardTarget } from "./agentRateCard";

/**
 * NONE — "does not have a business relationship with the licensed mortgage
 * company or exempt company to which your loan is referred". The form's second
 * box, and the default for every agent.
 *
 * HAS — the first box, which obliges a written description.
 */
export type DualCapacityRelationship = "NONE" | "HAS";

export interface AgentDisclosure {
  agent_id: string;
  mortgage_re_relationship: DualCapacityRelationship;
  /** Prints across the form's four description rules. Required when HAS. */
  mortgage_re_relationship_detail: string | null;
  /** Company name, MLD licence number and NMLS id — the form asks for all three
   * in ONE blank, so this is one free-text box. Not the agent's own nmls_id. */
  mortgage_company_name: string | null;
  /** Null until the agent actually answers. Distinguishes a chosen "no
   * relationship" from one nobody ever asked about — only the second wants
   * chasing, and with one field for both there is no way to chase it. */
  mortgage_re_relationship_set_at: string | null;
}

/** A PATCH updates only what it names. Setting the relationship to NONE also
 * clears the description server-side — a description that no longer describes
 * anything would otherwise reappear the next time HAS was ticked. */
export type AgentDisclosureUpdate = Partial<
  Omit<AgentDisclosure, "agent_id" | "mortgage_re_relationship_set_at">
>;

function path(target: RateCardTarget = {}) {
  if (target.agentId)
    return `/admin/agent-disclosure?agent_id=${encodeURIComponent(target.agentId)}`;
  if (target.userId)
    return `/admin/agent-disclosure?user_id=${encodeURIComponent(target.userId)}`;
  return "/admin/agent-disclosure";
}

export const agentDisclosureService = {
  get(client: ApiClient, target?: RateCardTarget) {
    return client.request<AgentDisclosure>(path(target));
  },

  update(
    client: ApiClient,
    body: AgentDisclosureUpdate,
    target?: RateCardTarget,
  ) {
    return client.request<AgentDisclosure>(path(target), {
      method: "PATCH",
      body,
    });
  },
};
